import { Injectable, isDevMode, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError } from 'rxjs';
import { environment } from '@env/environment';

// types
import { IData } from '@core/interfaces/common/IData';
import { PreguntaFrecuenteResult } from '@core/interfaces/home/pregunta-frecuente';
import { ContactoAsistenciaResultDto } from '@core/interfaces/home/contacto-asistencia';
import { RentabilidadPromedioResultDto } from '@core/interfaces/home/rentabilidad-promedio';
import { SolicitudAfiliacionPlanComplementarioResultDto } from '@core/interfaces/solicitud/solicitud-afiliacion-plan-complementario';
import { MetodoPagoCiudadanoDto } from '@core/interfaces/ciudadano/ciudadano.interface';
import { ObtenerParametroConfiguracionResultDto } from '@core/interfaces/parametro/parametro.interface';
import { EstadoEnum } from '@core/enums/motivo-estado.enum';
import {
  FrecuenciaPagoDto,
  TipoObjetivoAhorroDto,
} from '@core/interfaces/catalogo/catalogo.interface';
import {
  CambiarCredencialesUsuarioRequestDto,
  CambiarCredencialesUsuarioResponseDto,
} from '@core/interfaces/authentication/ilogin';
import {
  ObtenerDatosUsuarioRequestDto,
  ObtenerDatosUsuarioResponseDto,
} from '@core/interfaces/usuario/usuario.interface';
import {
  RegistrarBitacoraEventoRequestDto,
  RegistrarBitacoraEventoResponseDto,
} from '@core/interfaces/bitacora-evento/bitacora-evento.interface';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private API_URL = environment.api_url;

  /** Datos del usuario consultados (p.ej. en perfil). Disponibles en toda la app. */
  readonly datosUsuario = signal<ObtenerDatosUsuarioResponseDto | null>(null);
  /** Id de instancia de la validación biométrica (Autentikar) más reciente. */
  readonly instanciaValidacionBiometrica = signal<string | null>(null);

  constructor(private http: HttpClient) {}

  public getPreguntasFrecuentes(): Observable<IData<PreguntaFrecuenteResult[]>> {
    return this.http.get<IData<PreguntaFrecuenteResult[]>>(
      `${this.API_URL}/api/v1/preguntas-frecuentes`,
    );
  }

  public getContactoAsistencia(entidadId: number): Observable<ContactoAsistenciaResultDto> {
    return this.http.get<ContactoAsistenciaResultDto>(
      `${this.API_URL}/api/v1/contacto-asistencia-afp`,
      {
        params: { entidadId: entidadId },
      },
    );
  }

  public getSolicitudAfiliacionRentabilidadPromedioPlanIndividual(): Observable<RentabilidadPromedioResultDto> {
    return this.http.get<RentabilidadPromedioResultDto>(
      `${this.API_URL}/api/v1/consultar-rentabilidad-promedio-plan-individual`,
    );
  }

  public getTipoObjetivosAhorro(): Observable<IData<TipoObjetivoAhorroDto[]>> {
    return this.http.get<IData<TipoObjetivoAhorroDto[]>>(
      `${this.API_URL}/api/v1/buscarTiposObjetivosAhorro`,
    );
  }

  public getFrecuenciasPago(): Observable<IData<FrecuenciaPagoDto[]>> {
    return this.http.get<IData<FrecuenciaPagoDto[]>>(
      `${this.API_URL}/api/v1/pago/buscar_frecuencias_pago`,
    );
  }

  public getMetodosPagoCiudadano(
    estado: string | string[] | null | undefined = [EstadoEnum.AC],
  ): Observable<IData<MetodoPagoCiudadanoDto[]>> {
    return this.http
      .get<IData<MetodoPagoCiudadanoDto[]>>(
        `${this.API_URL}/api/v1/buscar_metodos_pagos_ciudadano_por_ciudadano`,
        {
          params: {
            ...(estado && estado.length ? { estado } : {}),
          },
        },
      )
      .pipe(catchError(() => of({ data: [] })));
  }

  public getMetodosPagoCiudadanoPorId(
    metodo_pago_ciudadano_id: number,
  ): Observable<IData<MetodoPagoCiudadanoDto[]>> {
    return this.http.get<IData<MetodoPagoCiudadanoDto[]>>(
      `${this.API_URL}/api/v1/ciudadano/buscar_metodos_pagos_ciudadano_id/${metodo_pago_ciudadano_id}`,
    );
  }

  public getParametroConfiguracion(
    parametroConfiguracionId: string,
  ): Observable<IData<ObtenerParametroConfiguracionResultDto>> {
    return this.http.get<IData<ObtenerParametroConfiguracionResultDto>>(
      `${this.API_URL}/api/v1/parametro-configuracion`,
      {
        params: {
          parametroConfiguracionId,
        },
      },
    );
  }

  public cambiarCredencialesUsuario(model: CambiarCredencialesUsuarioRequestDto) {
    return this.http.post<CambiarCredencialesUsuarioResponseDto>(
      `${this.API_URL}/api/v1/usuarios/cambiar_credenciales_usuario`,
      model,
    );
  }

  public getDatosUsuario(
    payload: ObtenerDatosUsuarioRequestDto,
  ): Observable<IData<ObtenerDatosUsuarioResponseDto[]>> {
    return this.http.get<IData<ObtenerDatosUsuarioResponseDto[]>>(
      `${this.API_URL}/api/v1/usuarios`,
      {
        params: {
          indice: payload.indice,
          correo_electronico: payload.correo_electronico,
        },
      },
    );
  }

  public actualizarUsuario(
    payload: ObtenerDatosUsuarioRequestDto,
  ): Observable<IData<ObtenerDatosUsuarioResponseDto[]>> {
    return this.http.put<IData<ObtenerDatosUsuarioResponseDto[]>>(
      `${this.API_URL}/api/v1/usuario/actualizar_usuario`,
      payload,
    );
  }

  public registrarBitacoraEvento(
    payload: RegistrarBitacoraEventoRequestDto,
  ): Observable<RegistrarBitacoraEventoResponseDto> {
    return this.http.post<RegistrarBitacoraEventoResponseDto>(
      `${this.API_URL}/api/v1/bitacora-evento`,
      payload,
    );
  }
}
